"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import { useTranslation } from "react-i18next";
import productData from "@/data/productData.json";
import { ProductDataSource } from "@/types/product";
import { ProductVariant } from "@/hooks/useFunnelState";

import AddOnCoverages from "./price-customization/AddOnCoverages";
import DeductibleSelection from "./price-customization/DeductibleSelection";
import CoPayCoverageSelection from "./price-customization/CoPayCoverageSelection";
import ExampleCalculation from "./price-customization/ExampleCalculation";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Calendar, Info, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Flatpickr imports
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Instance } from "flatpickr";
import { getFlatpickrLocale } from "@/utils/flatpickrLocale";

const typedProductData: ProductDataSource = productData as ProductDataSource;

interface ProductCustomizationOptionsProps {
  petType: "cat" | "dog";
  petName: string;
  breed: string;
  age: number;
  onCustomizeProduct: (
    product: ProductVariant,
    finalPrice: number,
    preexistingCoverage: boolean,
    worldwideCoverage: boolean,
    yearlyDeductible: number,
    coPayCoveragePercentage: number,
    paymentInterval: 1 | 2 | 4 | 12,
    paymentMethod: "credit_card" | "ESR",
    startOfInsurance: Date
  ) => void;
  onBack: () => void;
  selectedProduct: ProductVariant | null;
  setSelectedProduct: (product: ProductVariant) => void; // Keep for consistency, though product selection is now in previous step
  preexistingCoverage: boolean;
  setPreexistingCoverage: (checked: boolean) => void;
  worldwideCoverage: boolean;
  setWorldwideCoverage: (checked: boolean) => void;
  yearlyDeductible: number;
  setYearlyDeductible: (deductible: number) => void;
  coPayCoveragePercentage: number;
  setCoPayCoveragePercentage: (percentage: number) => void;
  paymentInterval: 1 | 2 | 4 | 12;
  setPaymentInterval: (interval: 1 | 2 | 4 | 12) => void;
  paymentMethod: "credit_card" | "ESR";
  setPaymentMethod: (method: "credit_card" | "ESR") => void;
  startOfInsurance: Date;
  setStartOfInsurance: (date: Date) => void;
  currentApiPrice: number | null;
  isLoadingPrice: boolean;
  underwritingFailed: boolean; // New prop
  underwritingReason: string | null; // New prop
}

const ProductCustomizationOptions: React.FC<ProductCustomizationOptionsProps> = ({
  petType,
  petName,
  breed,
  age,
  onCustomizeProduct,
  onBack,
  selectedProduct,
  setPreexistingCoverage,
  preexistingCoverage,
  setWorldwideCoverage,
  worldwideCoverage,
  setYearlyDeductible,
  yearlyDeductible,
  setCoPayCoveragePercentage,
  coPayCoveragePercentage,
  paymentInterval,
  setPaymentInterval,
  paymentMethod,
  setPaymentMethod,
  startOfInsurance,
  setStartOfInsurance,
  currentApiPrice,
  isLoadingPrice,
  underwritingFailed, // Destructure new prop
  underwritingReason, // Destructure new prop
}) => {
  const { t, i18n } = useTranslation();
  const { showErrorDialog } = useErrorDialog();
  const fpInstanceRef = React.useRef<Instance | null>(null);
  const [startOfInsuranceError, setStartOfInsuranceError] = React.useState<string | null>(null);

  const validateStartDate = React.useCallback(
    (date: Date | null) => {
      if (!date) {
        setStartOfInsuranceError(t("priceCustomization.startOfInsurancePlaceholder"));
        return false;
      }

      const todayAtMidnight = new Date();
      todayAtMidnight.setHours(0, 0, 0, 0);
      const maxStartDate = new Date(todayAtMidnight);
      maxStartDate.setFullYear(maxStartDate.getFullYear() + 1);

      const startDate = new Date(date);
      startDate.setHours(0, 0, 0, 0);

      if (startDate < todayAtMidnight) {
        setStartOfInsuranceError(t("priceCustomization.validation.startOfInsuranceInPast"));
        return false;
      }
      if (startDate > maxStartDate) {
        setStartOfInsuranceError(t("priceCustomization.validation.startOfInsuranceTooFar"));
        return false;
      }
      setStartOfInsuranceError(null);
      return true;
    },
    [t]
  );

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Effect to enforce payment method for monthly interval
  React.useEffect(() => {
    if (paymentInterval === 12 && paymentMethod === "ESR") {
      setPaymentMethod("credit_card");
    }
  }, [paymentInterval, paymentMethod, setPaymentMethod]);

  const deductibleOptions = React.useMemo(() => {
    const speciesOpts = typedProductData.customizationOptions?.[petType];
    if (!speciesOpts) return [300];
    const ageGroup = age < 9 ? speciesOpts.upTo9Yrs : speciesOpts.Over9Yrs;
    const list = ageGroup?.annualDeductibles || [];
    return list.length ? list : [300];
  }, [petType, age]);

  const coPayCoverageOptions = React.useMemo(() => {
    const speciesOpts = typedProductData.customizationOptions?.[petType];
    if (!speciesOpts) return [80];
    const ageGroup = age < 9 ? speciesOpts.upTo9Yrs : speciesOpts.Over9Yrs;
    const list =
      age < 9
        ? ageGroup?.claimDeductiblePercentageUpTo9Yrs
        : ageGroup?.claimDeductiblePercentageOver9Yrs;
    return list && list.length ? list : [80];
  }, [petType, age]);

  const paymentIntervalOptions = [1, 2, 4, 12];
  const paymentMethodOptions = ["credit_card", "ESR"];

  const handleSubmit = () => {
    if (!selectedProduct || currentApiPrice === null) {
      showErrorDialog(t("priceCustomization.validation.selectProductVariant"));
      return;
    }
    if (!validateStartDate(startOfInsurance)) {
      return;
    }
    onCustomizeProduct(
      selectedProduct,
      currentApiPrice,
      preexistingCoverage,
      worldwideCoverage,
      yearlyDeductible,
      coPayCoveragePercentage,
      paymentInterval,
      paymentMethod,
      startOfInsurance
    );
  };

  const flatpickrOptions = React.useMemo(() => {
    return {
      dateFormat: "d.m.Y",
      altInput: true,
      altFormat: "d.m.Y",
      locale: getFlatpickrLocale(i18n.language),
      disableMobile: true,
      allowInput: true,
      clickOpens: false
    };
  }, [i18n.language]);

  const handleFlatpickrChange = (selectedDates: Date[], dateStr: string, instance: Instance) => {
    if (selectedDates.length > 0) {
      const selectedAtMidnight = new Date(selectedDates[0]);
      selectedAtMidnight.setHours(0, 0, 0, 0);
      setStartOfInsurance(selectedAtMidnight);
      validateStartDate(selectedAtMidnight);
    } else {
      setStartOfInsuranceError(t("priceCustomization.startOfInsurancePlaceholder"));
    }
  };

  const isButtonDisabled = isLoadingPrice || !selectedProduct || currentApiPrice === null || underwritingFailed;
  const isSecondaryButtonDisabled = isLoadingPrice;

  return (
    <>
      <Card className="w-full max-w-4xl mx-auto border-none shadow-none">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">
            {t("banner-header-2", { petName: petName?.trim() || t("common.yourPet") })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <DeductibleSelection
            yearlyDeductible={yearlyDeductible}
            setYearlyDeductible={setYearlyDeductible}
            deductibleOptions={deductibleOptions}
            isLoadingPrice={isLoadingPrice}
          />

          <CoPayCoverageSelection
            coPayCoveragePercentage={coPayCoveragePercentage}
            setCoPayCoveragePercentage={setCoPayCoveragePercentage}
            coPayCoverageOptions={coPayCoverageOptions}
            isLoadingPrice={isLoadingPrice}
          />

          <ExampleCalculation
            petType={petType}
            petName={petName}
            yearlyDeductible={yearlyDeductible}
            coPayCoveragePercentage={coPayCoveragePercentage}
          />

          <AddOnCoverages
            preexistingCoverage={preexistingCoverage}
            setPreexistingCoverage={setPreexistingCoverage}
            worldwideCoverage={worldwideCoverage}
            setWorldwideCoverage={setWorldwideCoverage}
            isLoadingPrice={isLoadingPrice}
          />

          <div className="space-y-6 border-t pt-6">
            <h3 className="text-2xl font-bold text-center">{t("priceCustomization.paymentOptions")}</h3>
            <div className="space-y-4">
              {/* Payment Interval */}
              <div className="space-y-2">
                <Label htmlFor="payment-interval" className="text-lg font-medium">
                  {t("closing-payment-frequency-header")}
                </Label>
                <Select
                  value={String(paymentInterval)}
                  onValueChange={(value) => setPaymentInterval(Number(value) as 1 | 2 | 4 | 12)}
                  disabled={isLoadingPrice}
                >
                  <SelectTrigger id="payment-interval" className="w-full">
                    <SelectValue placeholder={t("priceCustomization.selectPaymentInterval")} />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentIntervalOptions.map((interval) => (
                      <SelectItem key={interval} value={String(interval)}>
                        {t(`priceCustomization.paymentIntervalOptionsWithDiscount.${interval}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="payment-method" className="text-lg font-medium">
                    {t("priceCustomization.paymentMethod")}
                  </Label>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <Info className="h-4 w-4 text-muted-foreground" />
                        <span className="sr-only">{t("common.info")}</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t("priceCustomization.paymentMethodInfoTitle")}</DialogTitle>
                      <DialogDescription asChild>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: t("priceCustomization.paymentMethodInfoDescription") || "",
                          }}
                        />
                      </DialogDescription>
                      </DialogHeader>
                    </DialogContent>
                  </Dialog>
                </div>
                <Select
                  value={paymentMethod}
                  onValueChange={(value) => setPaymentMethod(value as "credit_card" | "ESR")}
                  disabled={isLoadingPrice}
                >
                  <SelectTrigger id="payment-method" className="w-full">
                    <SelectValue placeholder={t("priceCustomization.selectPaymentMethod")} />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethodOptions.map((method) => (
                      <SelectItem
                        key={method}
                        value={method}
                        disabled={paymentInterval === 12 && method === "ESR"} // Disable ESR if monthly
                      >
                        {t(`priceCustomization.paymentMethodOptions.${method}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start of Insurance Date */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="start-of-insurance" className="text-lg font-medium">
                    {t("priceCustomization.startOfInsurance")}
                  </Label>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <Info className="h-4 w-4 text-muted-foreground" />
                        <span className="sr-only">{t("common.info")}</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t("closing-grace-period-info-header")}</DialogTitle>
                        <DialogDescription>
                          {t("closing-grace-period-info-content")}
                        </DialogDescription>
                      </DialogHeader>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="relative">
                  <Flatpickr
                    key={i18n.language}
                    value={startOfInsurance}
                    options={flatpickrOptions}
                    onChange={handleFlatpickrChange}
                    onReady={(_, __, instance) => {
                      fpInstanceRef.current = instance;
                    }}
                    render={(fpProps, ref) => {
                      const { defaultValue } = fpProps as { defaultValue?: string };
                      return (
                        <Input
                          id="start-of-insurance"
                          placeholder={t("priceCustomization.startOfInsurancePlaceholder")}
                          disabled={isLoadingPrice}
                          className={cn(
                            "w-full pr-10",
                            startOfInsuranceError && "border-destructive focus-visible:ring-destructive"
                          )}
                          aria-invalid={Boolean(startOfInsuranceError)}
                          defaultValue={defaultValue}
                          ref={ref as React.Ref<HTMLInputElement>}
                          required
                        />
                      );
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                    onClick={() => fpInstanceRef.current?.open()}
                    disabled={isLoadingPrice}
                  >
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="sr-only">
                      {t("common.openCalendar", { defaultValue: "Kalender öffnen" })}
                    </span>
                  </Button>
                </div>
                {startOfInsuranceError && (
                  <p className="text-sm text-destructive">{startOfInsuranceError}</p>
                )}
                 <p className="text-xs text-muted-primary">
                  {t("priceCustomization.startOfInsuranceHint")}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between gap-4">
          <Button variant="outline" onClick={onBack} className="w-full" disabled={isSecondaryButtonDisabled}>
            {t("priceCustomization.back")}
          </Button>
          <Button onClick={handleSubmit} className="w-full bg-continue-button text-continue-button-foreground hover:bg-continue-button/90" disabled={isButtonDisabled}>
            {isLoadingPrice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("priceCustomization.continue")}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
};

export default ProductCustomizationOptions;
